import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import i18n from '~i18n';
import numberWithCommas from '~filters/numberWithCommas';
import {
    formatDay,
    formatGeneratedAt,
    formatRate,
    reportFileName
} from './download';
import { peakDay } from '../chart-data';

// Brand palette (mirrors src/assets/style/_variables.styl).
const COLORS = {
    navy: [2, 35, 65], // $theme.primary
    blue: [112, 150, 210], // $theme.action
    ink: [30, 61, 89], // $theme.info
    muted: [120, 130, 145],
    line: [225, 229, 235],
    zebra: [245, 247, 250],
    white: [255, 255, 255]
};

const MARGIN = 14;
const DASH = '—';

const t = (key, params) => i18n.t(`activityReport.${key}`, params);
const n = value =>
    value === null || value === undefined ? DASH : numberWithCommas(value);
const rate = (part, whole) => (whole ? formatRate(part / whole) : DASH);

const tableStyles = {
    theme: 'grid',
    styles: {
        font: 'helvetica',
        fontSize: 9,
        cellPadding: 2.5,
        lineColor: COLORS.line,
        lineWidth: 0.2,
        textColor: COLORS.ink
    },
    headStyles: {
        fillColor: COLORS.blue,
        textColor: COLORS.white,
        fontStyle: 'bold'
    },
    footStyles: {
        fillColor: COLORS.zebra,
        textColor: COLORS.navy,
        fontStyle: 'bold'
    },
    alternateRowStyles: { fillColor: COLORS.zebra }
};

/** Right-align every column except the first. */
const numericColumns = count => {
    const styles = {};
    for (let i = 1; i < count; i++) styles[i] = { halign: 'right' };
    return styles;
};

/**
 * Group the daily series into ISO-ish weeks (Monday start). Since a unique
 * user can appear on several days, weekly figures are "active days" and
 * "peak day" rather than a sum of uniques, which would double count.
 */
export function weeklyRows(daily) {
    const weeks = new Map();
    daily.forEach(day => {
        const start = dayjs(day.date)
            .startOf('week')
            .add(1, 'day');
        const key = start.format('YYYY-MM-DD');
        const week = weeks.get(key) || {
            start: key,
            end: start.add(6, 'day').format('YYYY-MM-DD'),
            days: 0,
            activeDays: 0,
            maxLogins: 0,
            maxMeditated: 0,
            loginDays: 0,
            meditatedDays: 0
        };
        week.days += 1;
        if (day.uniqueLogins || day.uniqueMeditated) week.activeDays += 1;
        week.loginDays += day.uniqueLogins;
        week.meditatedDays += day.uniqueMeditated;
        week.maxLogins = Math.max(week.maxLogins, day.uniqueLogins);
        week.maxMeditated = Math.max(week.maxMeditated, day.uniqueMeditated);
        weeks.set(key, week);
    });
    return [...weeks.values()];
}

function drawHeader(doc, report) {
    const width = doc.internal.pageSize.getWidth();
    const scope = report.business
        ? report.business.name
        : t('report.allBusinesses');

    doc.setFillColor(...COLORS.navy);
    doc.rect(0, 0, width, 30, 'F');

    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    doc.text(t('report.title'), MARGIN, 13);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(scope, MARGIN, 21);

    doc.setFontSize(10);
    doc.text(
        `${formatDay(report.dateFrom)} – ${formatDay(report.dateTo)}`,
        width - MARGIN,
        13,
        {
            align: 'right'
        }
    );
    doc.setFontSize(8);
    doc.text(formatGeneratedAt(report.generatedAt), width - MARGIN, 21, {
        align: 'right'
    });

    return 38;
}

function drawKpis(doc, report, y) {
    const width = doc.internal.pageSize.getWidth();
    const { members, uniqueLogins, uniqueMeditated } = report.totals;
    const peak = peakDay(report.daily);

    const tiles = [
        { value: n(members), label: t('summary.members') },
        {
            value: n(uniqueLogins),
            label: t('summary.uniqueLogins'),
            sub: rate(uniqueLogins, members)
        },
        {
            value: n(uniqueMeditated),
            label: t('summary.uniqueMeditated'),
            sub: rate(uniqueMeditated, members)
        },
        {
            value: rate(uniqueMeditated, uniqueLogins),
            label: t('summary.meditatedRate')
        },
        {
            value: peak ? formatDay(peak.date) : DASH,
            label: t('summary.peakDay'),
            sub: peak ? n(peak.uniqueLogins) : ''
        }
    ].filter(tile => tile.label !== t('summary.members') || members !== null);

    const gap = 3;
    const pad = 3.5;
    const tileWidth =
        (width - MARGIN * 2 - gap * (tiles.length - 1)) / tiles.length;
    const tileHeight = 27;

    tiles.forEach((tile, index) => {
        const x = MARGIN + index * (tileWidth + gap);
        doc.setDrawColor(...COLORS.line);
        doc.setFillColor(...COLORS.zebra);
        doc.roundedRect(x, y, tileWidth, tileHeight, 2, 2, 'FD');

        doc.setTextColor(...COLORS.navy);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(tile.value.length > 8 ? 12 : 16);
        doc.text(String(tile.value), x + pad, y + 10.5);

        doc.setFontSize(7);
        const subWidth = tile.sub ? doc.getTextWidth(tile.sub) + 2 : 0;
        if (tile.sub) {
            doc.setTextColor(...COLORS.blue);
            doc.text(tile.sub, x + tileWidth - pad, y + 17, { align: 'right' });
        }

        doc.setTextColor(...COLORS.muted);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(
            tile.label.toUpperCase(),
            tileWidth - pad * 2 - subWidth
        );
        doc.text(lines.slice(0, 2), x + pad, y + 17);
    });

    return y + tileHeight + 8;
}

/** Start a new page when fewer than `needed` mm remain below `y`. */
function ensureSpace(doc, y, needed) {
    const height = doc.internal.pageSize.getHeight();
    if (y + needed > height - 18) {
        doc.addPage();
        return 18;
    }
    return y;
}

function sectionTitle(doc, text, y) {
    y = ensureSpace(doc, y, 40);
    doc.setTextColor(...COLORS.navy);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(text, MARGIN, y);
    return y + 3;
}

function drawFooter(doc) {
    const pages = doc.getNumberOfPages();
    const width = doc.internal.pageSize.getWidth();
    const height = doc.internal.pageSize.getHeight();

    for (let page = 1; page <= pages; page++) {
        doc.setPage(page);
        doc.setFontSize(8);
        doc.setTextColor(...COLORS.muted);
        doc.text(
            `${t('report.appName')} · ${t('report.title')}`,
            MARGIN,
            height - 8
        );
        doc.text(
            t('report.page', { page, pages }),
            width - MARGIN,
            height - 8,
            {
                align: 'right'
            }
        );
    }
}

/**
 * Build the PDF document for an activity report: KPI tiles, a summary
 * table, weekly figures and the daily breakdown (days with activity only).
 * @param {object} report API response from /business/activity-report
 * @param {object} [doc] injectable jsPDF instance (tests)
 * @returns {jsPDF}
 */
export function buildPdf(
    report,
    doc = new jsPDF({ unit: 'mm', format: 'a4' })
) {
    const { members, uniqueLogins, uniqueMeditated } = report.totals;
    const activeDays = report.daily.filter(
        d => d.uniqueLogins || d.uniqueMeditated
    );
    const peak = peakDay(report.daily);

    let y = drawHeader(doc, report);
    y = drawKpis(doc, report, y);

    // Summary
    y = sectionTitle(doc, t('report.totals'), y);
    autoTable(doc, {
        ...tableStyles,
        startY: y,
        margin: { left: MARGIN, right: MARGIN, bottom: 16 },
        head: [[t('report.metric'), t('report.value')]],
        body: [
            [
                t('report.period'),
                `${report.dateFrom} – ${report.dateTo} (${report.daily.length})`
            ],
            ...(members === null ? [] : [[t('summary.members'), n(members)]]),
            [t('summary.uniqueLogins'), n(uniqueLogins)],
            ...(members === null
                ? []
                : [[t('report.loginRate'), rate(uniqueLogins, members)]]),
            [t('summary.uniqueMeditated'), n(uniqueMeditated)],
            ...(members === null
                ? []
                : [
                      [
                          t('report.meditatedShare'),
                          rate(uniqueMeditated, members)
                      ]
                  ]),
            [t('summary.meditatedRate'), rate(uniqueMeditated, uniqueLogins)],
            [
                t('report.activeDays'),
                `${activeDays.length} / ${report.daily.length}`
            ],
            [
                t('summary.peakDay'),
                peak
                    ? `${formatDay(peak.date)} (${n(peak.uniqueLogins)})`
                    : DASH
            ]
        ],
        columnStyles: { 0: { cellWidth: 70 }, 1: { halign: 'right' } }
    });
    y = doc.lastAutoTable.finalY + 8;

    // Weekly figures
    const weeks = weeklyRows(report.daily);
    if (weeks.length > 1 && activeDays.length) {
        y = sectionTitle(doc, t('report.weekly'), y);
        autoTable(doc, {
            ...tableStyles,
            startY: y,
            margin: { left: MARGIN, right: MARGIN, bottom: 16 },
            head: [
                [
                    t('report.week'),
                    t('report.activeDays'),
                    t('report.loginDays'),
                    t('report.maxLogins'),
                    t('report.meditatedDays'),
                    t('report.maxMeditated')
                ]
            ],
            body: weeks.map(w => [
                `${dayjs(w.start).format('MMM D')} – ${dayjs(w.end).format(
                    'MMM D'
                )}`,
                `${w.activeDays} / ${w.days}`,
                n(w.loginDays),
                n(w.maxLogins),
                n(w.meditatedDays),
                n(w.maxMeditated)
            ]),
            columnStyles: { ...numericColumns(6), 0: { cellWidth: 38 } }
        });
        y = doc.lastAutoTable.finalY + 8;
    }

    // Daily breakdown, active days only
    y = sectionTitle(doc, t('report.daily'), y);
    autoTable(doc, {
        ...tableStyles,
        startY: y,
        margin: { left: MARGIN, right: MARGIN, bottom: 16 },
        head: [
            [
                t('table.date'),
                t('table.uniqueLogins'),
                t('table.uniqueMeditated')
            ]
        ],
        body: activeDays.length
            ? activeDays.map(day => [
                  dayjs(day.date).format('ddd, MMM D, YYYY'),
                  n(day.uniqueLogins),
                  n(day.uniqueMeditated)
              ])
            : [[t('table.allDaysEmpty'), DASH, DASH]],
        foot: [
            [
                `${t('report.totals')} (${t('report.uniqueUsers')})`,
                { content: n(uniqueLogins), styles: { halign: 'right' } },
                { content: n(uniqueMeditated), styles: { halign: 'right' } }
            ]
        ],
        showFoot: 'lastPage',
        columnStyles: numericColumns(3)
    });

    if (report.daily.length - activeDays.length > 0) {
        doc.setFontSize(8);
        doc.setTextColor(...COLORS.muted);
        doc.text(
            t('table.emptyDaysHidden', {
                count: report.daily.length - activeDays.length
            }),
            MARGIN,
            doc.lastAutoTable.finalY + 5
        );
    }

    drawFooter(doc);
    return doc;
}

/**
 * Build and download the PDF.
 * @param {object} report
 * @returns {string} the file name used
 */
export function exportPdf(report) {
    const fileName = reportFileName(report, 'pdf');
    buildPdf(report).save(fileName);
    return fileName;
}
